import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import DefaultActions from "../../components/Card/DefaultActions";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import ResponsivePagination from "../../components/ResponsivePagination";
import {
  BonusBatchViewModel,
  BonusHistoryViewModel,
  BonusSelectionOption,
  ListQuery,
  ProgressResultViewModel,
} from "../../Types/ExportTypes";

const BonusBatchPage: React.FC = () => {
  const { authService } = useAuth();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const { showSnackbar } = useSnackbar();
  const { navigate } = useNavigation();
  const { t } = useTranslation();

  const [bonusSettings, setBonusSettings] = useState<BonusSelectionOption[]>([]);
  const [bonusGroups, setBonusGroups] = useState<BonusSelectionOption[]>([]);
  const [selectedSetting, setSelectedSetting] = useState<number | "">("");
  const [selectedGroup, setSelectedGroup] = useState<number | "">("");
  const [fromDate, setFromDate] = useState<string>("");
  const [untilDate, setUntilDate] = useState<string>(new Date().toISOString().slice(0, 10));

  const [batches, setBatches] = useState<BonusBatchViewModel[]>([]);
  const [batchCount, setBatchCount] = useState(0);
  const [history, setHistory] = useState<BonusHistoryViewModel[]>([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [activeBatchId, setActiveBatchId] = useState<number | null>(null);
  const [historyFilter, setHistoryFilter] = useState("");
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const [batchQuery, setBatchQuery] = useCachedListQuery({ page: 0, pageSize: 5, filter: "", filters: [] });
  const [historyQuery, setHistoryQuery] = useCachedListQuery({ page: 0, pageSize: 10, filter: "", filters: [] });

  const selectedBatch = useMemo(
    () => batches.find((batch) => batch.id === activeBatchId) ?? null,
    [activeBatchId, batches]
  );

  const formattedUntilDate = useMemo(
    () => (untilDate ? new Date(untilDate).toISOString().slice(0, 10) : ""),
    [untilDate]
  );

  const formattedFromDate = useMemo(
    () => (fromDate ? new Date(fromDate).toISOString().slice(0, 10) : ""),
    [fromDate]
  );



  useEffect(() => {
    const loadSettings = async () => {
      const response = await authService.get<{ bonusSettings: BonusSelectionOption[]; bonusGroups: BonusSelectionOption[] }>("/bonusApi/Settings");
      setBonusSettings(response.bonusSettings);
      setBonusGroups(response.bonusGroups);
    };
    loadSettings();
  }, [authService]);

  const fetchBatches = async (query: ListQuery) => {
    showLoading();
    try {
      const response = await authService.post<{ items: BonusBatchViewModel[]; count: number }>("/bonusApi/BatchList", query);
      setBatches(response.items);
      setBatchCount(response.count);
    } finally {
      hideLoading();
    }
  };

  const fetchHistory = async () => {
    if (!activeBatchId || !historyDialogOpen) return;
    showLoading();
    try {
      const response = await authService.post<{ items: BonusHistoryViewModel[]; count: number }>(
        `/bonusApi/BatchHistory?batchId=${activeBatchId}`,
        historyQuery
      );
      setHistory(response.items);
      setHistoryCount(response.count);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    fetchBatches(batchQuery);
  }, [batchQuery]);

  useEffect(() => {
    fetchHistory();
  }, [historyQuery, activeBatchId, historyDialogOpen]);

  const handleCreate = async () => {
    if (!selectedSetting || !selectedGroup) {
      showSnackbar(t("dialog.requiredFields"), "warning");
      return;
    }

    showLoading();
    try {
      const payload = {
        bonusSettingsId: selectedSetting,
        bonusGroupId: selectedGroup,
        fromDate: formattedFromDate || null,
        untilDate: formattedUntilDate || null,
      };
      const response = await authService.post<{ batchId: number }>("/bonusApi/CreateBatch", payload);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
      setActiveBatchId(response.batchId);
      setHistoryQuery({ ...historyQuery, page: 0 });
      setBatchQuery({ ...batchQuery, page: 0 });
      setRunDialogOpen(false);
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const handleHistorySearch = (event: React.FormEvent) => {
    event.preventDefault();
    setHistoryQuery({ ...historyQuery, filter: historyFilter, page: 0 });
  };

  const openHistoryDialog = (batchId: number) => {
    setActiveBatchId(batchId);
    setHistoryQuery({ ...historyQuery, page: 0 });
    setHistoryDialogOpen(true);
  };

  const closeHistoryDialog = () => {
    setHistoryDialogOpen(false);
  };

  const pollStatus = async (progressId: number, successMessage: string) => {
    const interval = 3000;
    let attempts = 200;

    while (attempts > 0) {
      try {
        const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);

        if (!response) throw new Error(t("dialog.fetchStatusError"));
        if (response.error) {
          showSnackbar(`${response.text ?? t("dialog.fetchStatusError")}`, "error");
          break;
        }

        if (response.done) {
          showSnackbar(successMessage, "success");
          await fetchHistory();
          await fetchBatches(batchQuery);
          break;
        }
      } catch (error: any) {
        showSnackbar(error.response?.data ?? error.message, "error");
        break;
      }

      attempts--;
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  };

  const handleProcessBatch = async () => {
    if (!activeBatchId) {
      showSnackbar(t("common.selectBatch"), "warning");
      return;
    }

    if (selectedBatch?.processed) {
      showSnackbar(t("common.processed"), "info");
      return;
    }

    showLoading();
    try {
      const response = await authService.post<{ progressId: number }>("/bonusApi/ProcessBatch", { id: activeBatchId });
      await pollStatus(response.progressId, t("dialog.savedSuccessfully"));
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  const handleEmailBatch = async () => {
    if (!activeBatchId) {
      showSnackbar(t("common.selectBatch"), "warning");
      return;
    }

    if (!selectedBatch?.processed) {
      showSnackbar(t("common.unprocessed"), "warning");
      return;
    }

    showLoading();
    try {
      const response = await authService.post<{ progressId: number }>("/bonusApi/EmailBatch", { id: activeBatchId });
      await pollStatus(response.progressId, t("dialog.savedSuccessfully"));
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
    }
  };

  return (
    <CenteredListCard title={t("common.bonusBatch")}> 
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <DefaultActions
          customActions={[
            {
              label: t("common.runBatch"),
              onClick: () => setRunDialogOpen(true),
            },
            {
              label: t("common.bonusSystem"),
              onClick: () => navigate({ path: "/sales/bonussystem/list" }),
            },
          ]}
        />
      </Stack>

      <Card sx={{ width: "100%", mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("common.batches")}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {batches.map((batch) => (
              <Grid item xs={12} md={6} lg={4} key={batch.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      #{batch.id} - {batch.bonusSettingName}
                    </Typography>
                    <Typography variant="body2">{batch.bonusGroupName}</Typography>
                    <Typography variant="body2">
                      {(() => {
                        const d = new Date(batch.runTime);
                        if (isNaN(d.getTime())) return "";

                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        const dd = String(d.getDate()).padStart(2, "0");
                        const hh = String(d.getHours()).padStart(2, "0");
                        const min = String(d.getMinutes()).padStart(2, "0");

                        return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
                      })()}
                    </Typography>
                    <Typography variant="body2">
                      {batch.processed ? t("common.processed") : t("common.unprocessed")}
                    </Typography>
                    <Button
                      fullWidth
                      sx={{ mt: 1 }}
                      variant={batch.id === activeBatchId ? "contained" : "outlined"}
                      onClick={() => openHistoryDialog(batch.id)}
                    >
                      {t("common.view")}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {batches.length === 0 && (
              <Grid item xs={12}>
                <Typography align="center">{t("common.noEntriesFound")}</Typography>
              </Grid>
            )}
          </Grid>
          <ResponsivePagination
            page={batchQuery.page}
            pageSize={batchQuery.pageSize}
            totalCount={batchCount}
            onPageChange={(page) => setBatchQuery({ ...batchQuery, page })}
          />
        </CardContent>
      </Card>

      <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t("common.runBatch")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                required
                fullWidth
                label={t("common.bonusSystem")}
                value={selectedSetting}
                onChange={(e) => setSelectedSetting(Number(e.target.value))}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  {t("common.select")}
                </MenuItem>
                {bonusSettings.map((setting) => (
                  <MenuItem key={setting.id} value={setting.id}>
                    {setting.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                required
                fullWidth
                label={t("common.bonusGroup")}
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(Number(e.target.value))}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">
                  {t("common.select")}
                </MenuItem>
                {bonusGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t("common.from")}
                value={formattedFromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t("common.to")}
                value={formattedUntilDate}
                onChange={(e) => setUntilDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreate} disabled={isLoading}>
            {t("common.runBatch")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialogOpen} onClose={closeHistoryDialog} fullWidth maxWidth="lg">
        <DialogTitle>{t("common.results")}</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2} sx={{mt:1}}>
            <Typography variant="subtitle1">{t("common.selectBatch")}</Typography>
            <Box component="form" onSubmit={handleHistorySearch} sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label={t("common.search")}
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button type="submit" variant="contained" disabled={isLoading}>
                {t("common.search")}
              </Button>
            </Box>
          </Stack>
          {activeBatchId != null && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mt: 2 }}
            >
              <Button
                variant="contained"
                onClick={handleProcessBatch}
                disabled={isLoading || selectedBatch?.processed}
                color="success"
              >
                {t("common.run")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleEmailBatch}
                disabled={isLoading || !selectedBatch?.processed}
              >
                {t("common.sendEmail")}
              </Button>
            </Stack>
          )}
          {activeBatchId == null ? (
            <Typography sx={{ mt: 2 }}>{t("common.selectBatch")}</Typography>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {history.map((entry) => (
                  <Grid item xs={12} md={6} lg={4} key={entry.id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {entry.customerName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("common.customerNo")}: {entry.customerNo}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {entry.processed ? t("common.processed") : t("common.pending")}
                          </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {t("common.bonusPoints")}: <strong>{entry.bonusPoints}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.remaining")}  {t("common.bonusPoints")}: {entry.remainingBonusPoints}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.documentNo")}: {entry.documentNumber || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.code")}: {entry.certificateCode || "-"}
                        </Typography>
                        {entry.certificateCode && (
                          <Typography variant="body2">
                           <strong> {entry.certificateAmount} {entry.certificateCurrencyCode}</strong>
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {history.length === 0 && (
                  <Grid item xs={12}>
                    <Typography align="center">{t("common.noEntriesFound")}</Typography>
                  </Grid>
                )}
              </Grid>
              <ResponsivePagination
                page={historyQuery.page}
                pageSize={historyQuery.pageSize}
                totalCount={historyCount}
                onPageChange={(page) => setHistoryQuery({ ...historyQuery, page })}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHistoryDialog}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </CenteredListCard>
  );
};

export default BonusBatchPage;
